import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { paymentsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/lib/utils';
import { CurrencyInr, Bank, User, MapPin, Check, X, Hash, Receipt, DownloadSimple, PencilSimple, Trash, ArrowUUpLeft, ArrowsClockwise } from '@phosphor-icons/react';

const PAYMENT_MODES = ['Cash', 'UPI', 'Bank Transfer', 'Card', 'Credit Card', 'Debit Card', 'Cheque', 'Razorpay', 'PayTM', 'Net Banking'];

/**
 * Reusable Payment detail dialog with admin operations:
 * receipt download, reconcile, edit, refund, delete / dissociate-to-credit.
 * Pass `admin` to show the operations and `onChanged` to refresh the list.
 */
export default function PaymentDetailDialog({ payment, open, onClose, admin = false, banks = [], onChanged }) {
  const [busy, setBusy] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [refunding, setRefunding] = useState(false);
  const [refundForm, setRefundForm] = useState({ refund_date: new Date().toISOString().split('T')[0], refund_mode: '', reference: '', notes: '' });

  if (!payment) return null;
  const allocations = payment.invoice_payments || [];
  const allocated = allocations.reduce((s, a) => s + (a.invoice_amount || a.amount_applied || 0), 0);
  const status = payment.status;

  const done = (msg) => { toast.success(msg); onChanged?.(); onClose?.(); };

  const startEdit = () => {
    setForm({
      amount: payment.amount, payment_date: payment.payment_date || '',
      payment_mode: payment.payment_mode || '', deposit_to: payment.deposit_to || payment.account_name || '',
      reference_number: payment.reference_number || payment.reference || '', notes: payment.notes || payment.description || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setBusy('edit');
    try {
      await paymentsAPI.edit(payment.id, { ...form, amount: Number(form.amount) });
      done('Payment updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setBusy(''); }
  };

  const doReconcile = async () => {
    setBusy('reconcile');
    try { await paymentsAPI.reconcile(payment.id, !payment.reconciled); done(payment.reconciled ? 'Marked unreconciled' : 'Marked reconciled'); }
    catch (e) { toast.error('Failed'); } finally { setBusy(''); }
  };

  const doReceipt = async () => {
    setBusy('receipt');
    try {
      const res = await paymentsAPI.receiptPdf(payment.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) { toast.error('Failed to generate receipt'); } finally { setBusy(''); }
  };

  const doDelete = async () => {
    if (!window.confirm('Delete this payment entirely? The invoice balance will be restored.')) return;
    setBusy('delete');
    try { await paymentsAPI.remove(payment.id); done('Payment deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); } finally { setBusy(''); }
  };

  const doDissociate = async () => {
    if (!window.confirm('Dissociate this payment from the invoice and keep the amount as customer credit?')) return;
    setBusy('dissociate');
    try { const { data } = await paymentsAPI.dissociateCredit(payment.id); done(`Converted to customer credit (₹${data.credit.amount})`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(''); }
  };

  const doRefund = async () => {
    setBusy('refund');
    try { await paymentsAPI.refund(payment.id, refundForm); done('Payment refunded'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to refund'); } finally { setBusy(''); }
  };

  const bankOptions = banks.length ? banks.map(b => b.account_name) : PAYMENT_MODES;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setEditing(false); setRefunding(false); onClose?.(); } }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="payment-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center gap-2 flex-wrap">
            <CurrencyInr size={20} className="text-emerald-600" />
            <span>Payment #{payment.payment_number || payment.id}</span>
            {status === 'refunded' && <span className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded ml-1">Refunded</span>}
            {status === 'dissociated' && <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded ml-1">Credit</span>}
            {payment.reconciled ? (
              <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded ml-1"><Check size={12} weight="bold" /> Reconciled</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded ml-1"><X size={12} weight="bold" /> Unreconciled</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {admin && !editing && !refunding && (
          <div className="flex flex-wrap gap-2 border-b border-[#E2E8F0] pb-3" data-testid="payment-actions">
            <Button variant="outline" size="sm" onClick={doReceipt} disabled={!!busy} data-testid="pay-receipt-btn"><DownloadSimple size={14} className="mr-1" />Receipt</Button>
            <Button variant="outline" size="sm" onClick={doReconcile} disabled={!!busy} data-testid="pay-reconcile-btn"><ArrowsClockwise size={14} className="mr-1" />{payment.reconciled ? 'Unreconcile' : 'Reconcile'}</Button>
            {status !== 'refunded' && status !== 'dissociated' && (
              <>
                <Button variant="outline" size="sm" onClick={startEdit} disabled={!!busy} className="text-blue-600 border-blue-200 hover:bg-blue-50" data-testid="pay-edit-btn"><PencilSimple size={14} className="mr-1" />Edit</Button>
                <Button variant="outline" size="sm" onClick={() => setRefunding(true)} disabled={!!busy} className="text-orange-600 border-orange-200 hover:bg-orange-50" data-testid="pay-refund-btn"><ArrowUUpLeft size={14} className="mr-1" />Refund</Button>
                <Button variant="outline" size="sm" onClick={doDissociate} disabled={!!busy} className="text-purple-600 border-purple-200 hover:bg-purple-50" data-testid="pay-dissociate-btn">Dissociate &amp; Add as Credit</Button>
                <Button variant="outline" size="sm" onClick={doDelete} disabled={!!busy} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="pay-delete-btn"><Trash size={14} className="mr-1" />Delete</Button>
              </>
            )}
          </div>
        )}

        {admin && editing && (
          <div className="space-y-3 border-b border-[#E2E8F0] pb-4" data-testid="payment-edit-form">
            <p className="text-sm font-medium text-[#0F172A]">Edit Payment</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#64748B] block mb-1">Amount</label><Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="edit-pay-amount" /></div>
              <div><label className="text-xs text-[#64748B] block mb-1">Date</label><Input type="date" value={form.payment_date} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} data-testid="edit-pay-date" /></div>
              <div><label className="text-xs text-[#64748B] block mb-1">Mode</label>
                <Select value={form.payment_mode} onValueChange={v => setForm(f => ({ ...f, payment_mode: v }))}><SelectTrigger data-testid="edit-pay-mode"><SelectValue placeholder="Mode" /></SelectTrigger><SelectContent>{PAYMENT_MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><label className="text-xs text-[#64748B] block mb-1">Deposit To</label>
                <Select value={form.deposit_to} onValueChange={v => setForm(f => ({ ...f, deposit_to: v }))}><SelectTrigger data-testid="edit-pay-deposit"><SelectValue placeholder="Account" /></SelectTrigger><SelectContent>{bankOptions.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="col-span-2"><label className="text-xs text-[#64748B] block mb-1">Reference</label><Input value={form.reference_number} onChange={e => setForm(f => ({ ...f, reference_number: e.target.value }))} data-testid="edit-pay-ref" /></div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="sm" className="bg-[#0F4C81] text-white" onClick={saveEdit} disabled={busy === 'edit'} data-testid="edit-pay-save">{busy === 'edit' ? 'Saving...' : 'Save'}</Button>
            </div>
          </div>
        )}

        {admin && refunding && (
          <div className="space-y-3 border-b border-[#E2E8F0] pb-4" data-testid="payment-refund-form">
            <p className="text-sm font-medium text-[#0F172A]">Refund {formatCurrency(payment.amount)} to {payment.customer_name}</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#64748B] block mb-1">Refund Date</label><Input type="date" value={refundForm.refund_date} onChange={e => setRefundForm(f => ({ ...f, refund_date: e.target.value }))} data-testid="refund-date" /></div>
              <div><label className="text-xs text-[#64748B] block mb-1">Mode</label><Input value={refundForm.refund_mode} onChange={e => setRefundForm(f => ({ ...f, refund_mode: e.target.value }))} placeholder="e.g. Bank Transfer" data-testid="refund-mode" /></div>
              <div className="col-span-2"><label className="text-xs text-[#64748B] block mb-1">Reference / Notes</label><Input value={refundForm.notes} onChange={e => setRefundForm(f => ({ ...f, notes: e.target.value }))} data-testid="refund-notes" /></div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setRefunding(false)}>Cancel</Button>
              <Button size="sm" className="bg-orange-600 text-white hover:bg-orange-700" onClick={doRefund} disabled={busy === 'refund'} data-testid="refund-submit">{busy === 'refund' ? 'Processing...' : 'Confirm Refund'}</Button>
            </div>
          </div>
        )}

        <div className="space-y-4 py-2" data-testid="payment-detail-content">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wide text-emerald-700">Amount Received</p>
              <p className="text-base font-bold text-emerald-700">{formatCurrency(payment.amount || 0)}</p>
            </div>
            <div className="bg-[#F8FAFC] rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wide text-[#64748B]">Date</p>
              <p className="text-sm font-medium">{formatDate(payment.payment_date || payment.date)}</p>
            </div>
            <div className="bg-[#F8FAFC] rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wide text-[#64748B]">Mode</p>
              <p className="text-sm font-medium capitalize">{(payment.payment_mode || payment.mode || '-').replace(/_/g, ' ')}</p>
            </div>
            <div className="bg-[#F8FAFC] rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wide text-[#64748B]">Deposit To</p>
              <p className="text-sm font-medium flex items-center gap-1"><Bank size={12} />{payment.account_name || payment.deposit_to || '-'}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="border border-[#E2E8F0] rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wide text-[#64748B] flex items-center gap-1 mb-1"><User size={12} />Customer</p>
              <p className="font-medium text-[#0F172A]">{payment.customer_name || '-'}</p>
              {payment.customer_id && <p className="text-[10px] text-[#64748B] font-mono">ID: {payment.customer_id}</p>}
            </div>
            <div className="border border-[#E2E8F0] rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wide text-[#64748B] flex items-center gap-1 mb-1"><Hash size={12} />Reference</p>
              <p className="font-medium text-[#0F172A]">{payment.reference_number || payment.reference || payment.cf_card_cheque_number || '-'}</p>
              {payment.invoice_number && <p className="text-xs text-[#64748B]">Invoice: <span className="font-mono">{payment.invoice_number}</span></p>}
              {payment.location_name && <p className="text-xs text-[#64748B] flex items-center gap-1"><MapPin size={10} />{payment.location_name}</p>}
            </div>
          </div>

          {allocations.length > 0 ? (
            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="payment-allocations">
              <div className="bg-[#F8FAFC] px-3 py-2 border-b border-[#E2E8F0]">
                <h3 className="text-sm font-medium flex items-center gap-1"><Receipt size={14} />Applied to Invoices ({allocations.length})</h3>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-white text-[#64748B] border-b border-[#E2E8F0]">
                    <th className="text-left p-2 font-medium">Invoice #</th>
                    <th className="text-left p-2 font-medium">Invoice Date</th>
                    <th className="text-right p-2 font-medium">Amount Applied</th>
                    <th className="text-right p-2 font-medium">TDS</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map((a, i) => (
                    <tr key={a.invoice_payment_id || i} className="border-t border-[#E2E8F0]">
                      <td className="p-2 font-mono text-[#0F4C81]">{a.invoice_number || a.invoice_id || '-'}</td>
                      <td className="p-2 text-[#64748B]">{formatDate(a.invoice_date)}</td>
                      <td className="p-2 text-right font-medium">{formatCurrency(a.invoice_amount || a.amount_applied || 0)}</td>
                      <td className="p-2 text-right text-[#64748B]">{a.withholding_tax_amount ? formatCurrency(a.withholding_tax_amount) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-xs text-amber-700">
              {payment.invoice_number ? <span>Linked Invoice: <strong className="font-mono">{payment.invoice_number}</strong></span> : 'No invoice allocation recorded for this payment.'}
            </div>
          )}

          {(payment.description || payment.notes) && (
            <div className="border border-[#E2E8F0] rounded-lg p-3 text-sm">
              <span className="text-[#64748B] text-xs">Notes: </span>{payment.description || payment.notes}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
